import { useState, useEffect } from "react";
import { parseUnits, etherUnits, Address, formatUnits } from "viem";
import { useAccount, useReadContract } from "wagmi";
import { contractAddresses, WstUSCAbi } from "@meta";
import {
  useWingsContractWrite,
  useNotificationContext,
  StandardNotifBody,
  TransactionComplete,
  Transfer,
  TransferMoney,
  DisplayErrorNotif,
  StepsContainer,
  ApproveComplete,
  FlexCol,
  InputField,
  FlexRow,
  Button,
  DisplayableInputField,
  SVGWrapper,
} from "@shared";
import { formatMoney, displayTokens, formatUnitsToMaxValue } from "@utils";
import { useButtonPropsOverride } from "../../../../../../state/across/buttonPropsOverride";
import { useStepState } from "../../../../../../state/common/useStepState.ui";
import { useGetWalletBalanceByToken } from "../../../../../../state/erc20/useGetWalletBalanceByToken";
import { useSpotPrice } from "../../../../../../state/usc/useSpotPrice";

/* ----------- */
/*    Icons    */
/* ----------- */
import StUscLogo from "@assets/tokens/stUsc.svg";
import WstUSCLogo from "@assets/tokens/wstUsc.svg";
import { useFetchWstUscPrice } from "../../../../../../state/wstUsc/useFetchWstUscPrice";
import { useWingsApprove } from "../../../../../../state/common/useWingsApprove";

export const useFetchConvertedWstUsc = (stUscValue: string) => {
  const [convertedWstUscValue, setConvertedWstUscValue] = useState("");

  const { data, isFetching: isWstUscFetching } = useReadContract({
    abi: WstUSCAbi,
    address: contractAddresses.WstUSC,
    functionName: "previewDeposit",
    args: [parseUnits(String(stUscValue || 0), etherUnits.wei)],
  });

  useEffect(() => {
    if (data) {
      setConvertedWstUscValue(formatUnits(data, 18));
    } else {
      setConvertedWstUscValue("");
    }
  }, [data, stUscValue]);

  return { convertedWstUscValue, isWstUscFetching };
};

export const useFetchConvertedStUsc = (wstUscValue: string) => {
  const [convertedStUscValue, setConvertedStUscValue] = useState("");

  const { data, isFetching: isStUscFetching } = useReadContract({
    abi: WstUSCAbi,
    address: contractAddresses.WstUSC,
    functionName: "previewMint",
    args: [parseUnits(String(wstUscValue || 0), etherUnits.wei)],
  });

  useEffect(() => {
    if (data) {
      setConvertedStUscValue(formatUnits(data, 18));
    } else {
      setConvertedStUscValue("");
    }
  }, [data, wstUscValue]);

  return { convertedStUscValue, isStUscFetching };
};

export const WrapForm = () => {
  const { isConnected, address } = useAccount();
  const uscPrice = useSpotPrice(contractAddresses.USC);

  const { writeContractAsync: wrapAsync, isPending: isStaking } =
    useWingsContractWrite();

  const [wstUscValue, setWstUscValue] = useState("");
  const [stUscValue, setStUscValue] = useState("");
  const [lastUpdatedField, setLastUpdatedField] = useState<
    null | "stUsc" | "wstUsc"
  >(null);
  const { approveAsync, isApproving, isApproved } = useWingsApprove(
    contractAddresses.USCStaking,
    contractAddresses.WstUSC,
    parseUnits(String(stUscValue), etherUnits.wei)
  );

  const { showNotification } = useNotificationContext();
  const { steps, currentStep, finishSteps, setCurrentStep } = useStepState(
    ["Approve", "Wrap"],
    isApproved ? 1 : 0
  );
  useEffect(() => {
    if (isApproved) {
      setCurrentStep(1);
    } else setCurrentStep(0);
  }, [isApproved]);

  const { convertedWstUscValue, isWstUscFetching } =
    useFetchConvertedWstUsc(stUscValue);

  const { convertedStUscValue, isStUscFetching } =
    useFetchConvertedStUsc(wstUscValue);

  useEffect(() => {
    if (lastUpdatedField === "stUsc") {
      setWstUscValue(convertedWstUscValue);
    }
  }, [convertedWstUscValue, lastUpdatedField]);

  useEffect(() => {
    if (lastUpdatedField === "wstUsc") {
      setStUscValue(convertedStUscValue);
    }
  }, [convertedStUscValue, lastUpdatedField]);

  const onStUscChange = (value: string) => {
    setStUscValue(value);
    setLastUpdatedField("stUsc");
  };

  const onWstUscValueChange = (value: string) => {
    setWstUscValue(value);
    setLastUpdatedField("wstUsc");
  };

  const wrapFunction = async () => {
    await wrapAsync(
      {
        abi: WstUSCAbi,
        address: contractAddresses.WstUSC,
        functionName: "deposit",
        args: [parseUnits(String(stUscValue), etherUnits.wei), address!],
      },
      {
        onSuccess: (txHash?: Address) => {
          finishSteps();
          showNotification({
            status: "success",
            txHash,
            content: (
              <StandardNotifBody
                headerComponent={<TransactionComplete />}
                transferComponent={
                  <Transfer
                    rightComponent={
                      <TransferMoney
                        icon={
                          <SVGWrapper src={WstUSCLogo} width={22} height={22} />
                        }
                        text={formatMoney(wstUscValue)}
                        symbol="wstUSC"
                      />
                    }
                    leftComponent={
                      <TransferMoney
                        icon={
                          <SVGWrapper src={StUscLogo} width={22} height={22} />
                        }
                        text={formatMoney(stUscValue)}
                        symbol="stUSC"
                      />
                    }
                  />
                }
              />
            ),
          });
          setWstUscValue("");
          setStUscValue("");
        },
      }
    );
  };

  const loadingNotif = (currentStep: number) => {
    showNotification({
      status: "loading",
      content: (
        <StandardNotifBody
          header={
            currentStep === 0 ? "Approve in Process" : "Transaction in Process"
          }
          loading
          transferComponent={
            <Transfer
              rightComponent={
                <TransferMoney
                  icon={<SVGWrapper src={WstUSCLogo} width={22} height={22} />}
                  text={formatMoney(wstUscValue)}
                  symbol="wstUSC"
                />
              }
              leftComponent={
                <TransferMoney
                  icon={<SVGWrapper src={StUscLogo} width={22} height={22} />}
                  text={formatMoney(stUscValue)}
                  symbol="stUSC"
                />
              }
            />
          }
          stepsComponent={
            <StepsContainer
              stepNames={steps}
              currentStep={currentStep}
              loading
            />
          }
        />
      ),
    });
  };

  const handleButtonClick = async () => {
    loadingNotif(currentStep);
    if (!isApproved) {
      await approveAsync(parseUnits(String(stUscValue), etherUnits.wei), {
        onSuccess: async (txHash?: string) => {
          showNotification({
            txHash,
            status: "success",
            content: (
              <StandardNotifBody
                headerComponent={<ApproveComplete />}
                transferComponent={
                  <Transfer
                    rightComponent={
                      <TransferMoney
                        icon={
                          <SVGWrapper src={WstUSCLogo} width={22} height={22} />
                        }
                        text={formatMoney(wstUscValue)}
                        symbol="wstUSC"
                      />
                    }
                    leftComponent={
                      <TransferMoney
                        icon={
                          <SVGWrapper src={StUscLogo} width={22} height={22} />
                        }
                        text={formatMoney(stUscValue)}
                        symbol="stUSC"
                      />
                    }
                  />
                }
                stepsComponent={
                  <StepsContainer
                    stepNames={steps}
                    currentStep={currentStep}
                    loading
                  />
                }
              />
            ),
          });
          setCurrentStep(1);
          loadingNotif(1);
          await wrapFunction();
        },
        onError: (message?: string) => {
          showNotification({
            status: "error",
            content: <DisplayErrorNotif message={message} />,
          });
        },
      });
    } else {
      await wrapFunction();
    }
  };

  const { data, isLoading } = useReadContract({
    abi: WstUSCAbi,
    address: contractAddresses.WstUSC,
    functionName: "balanceOf",
    args: [address!],
  });

  const wstUscBalance: DisplayableInputField = {
    value: displayTokens(data, {}),
    isFetched: !isLoading,
    label: "",
  };

  const { isLoading: isStUscBalanceLoading, balance: stUscBalance } =
    useGetWalletBalanceByToken(contractAddresses.USCStaking);

  const displayableStUscWalletBalance: DisplayableInputField = {
    value: displayTokens(stUscBalance, {}),
    isFetched: !isStUscBalanceLoading,
    label: "",
  };

  const { formattedPrice: wstUscPrice } = useFetchWstUscPrice();

  const displayableWstUscDollarValue: DisplayableInputField = {
    value: formatMoney(Number(wstUscValue) * wstUscPrice),
    label: "Value: $ ",
  };
  const displayableStUscDollarValue: DisplayableInputField = {
    value: formatMoney(Number(stUscValue) * Number(uscPrice?.spotPrice)),
    label: "Value: $ ",
  };

  const { getButtonPropsOverride } = useButtonPropsOverride();

  return (
    <FlexCol className="gap-1">
      <div className="md:mt-[18.74px] mt-[12.98px]">
        <InputField
          name="StUSCAmount"
          value={stUscValue}
          onChange={(e) => onStUscChange(e.target.value)}
          label="Wrap stUSC"
          placeholder={"Amount"}
          leftLabel={
            <FlexRow className="items-center gap-2">
              <SVGWrapper src={StUscLogo} width={24} height={24} />
            </FlexRow>
          }
          rightLabel={
            <button
              className="bg-primary-10 text-primary rounded-md w-16 h-9 px-4 py-2 flex items-center justify-center text-body-small-medium"
              disabled={!stUscBalance || !isConnected}
              onClick={() => {
                onStUscChange(formatUnitsToMaxValue(stUscBalance)?.toString());
              }}
            >
              MAX
            </button>
          }
          walletBalance={displayableStUscWalletBalance}
          dollarValue={displayableStUscDollarValue}
        />
      </div>
      <div className="md:mt-[18.74px] mt-[8.98px]">
        <InputField
          name="UscAmount"
          value={wstUscValue}
          onChange={(e) => onWstUscValueChange(e.target.value)}
          label="To Receive"
          placeholder={"Amount"}
          leftLabel={
            <FlexRow className="items-center gap-2">
              <SVGWrapper src={WstUSCLogo} width={24} height={24} />
            </FlexRow>
          }
          walletBalance={wstUscBalance}
          dollarValue={displayableWstUscDollarValue}
        />
      </div>

      <Button
        className="mt-[8.98px] md:mt-[19px]"
        onClick={handleButtonClick}
        disabled={
          !isConnected ||
          (!wstUscValue && !stUscValue) ||
          isWstUscFetching ||
          isStUscFetching
        }
        loading={isStaking || isApproving}
        fullWidth
        size="big"
        color="primary"
        {...getButtonPropsOverride(1)}
      >
        {Number(wstUscValue || 0) > 0 || Number(stUscValue || 0) > 0
          ? isApproved
            ? "Wrap stUSC"
            : "Approve"
          : "Enter amount"}
      </Button>
    </FlexCol>
  );
};
